import { Link, useNavigate } from 'react-router-dom'

// ** Custom Hooks
import { useSkin } from '@hooks/useSkin'
//import useJwt from '@src/auth/jwt/useJwt'
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react"
import { InteractionStatus } from '@azure/msal-browser'
import { useAuth } from "@auth"
import { useUserContext } from '@context/User'

// ** Third Party Components
import toast from 'react-hot-toast'

// ** Custom Components
import Avatar from '@components/avatar'
import BrandLogo from '@components/brand-logo'
import Spinner from '@src/@core/components/spinner/Fallback-spinner'
import UiLoader from '@components/ui-loader'
import Footer from '@src/@core/layouts/components/footer'

// ** Utils
import { mailTo, getHomeRouteForLoggedInUser } from '@utils'

// ** Reactstrap Imports
import { Row, Col, Form, Input, Label, Alert, Button, CardText, CardTitle, UncontrolledTooltip,
  UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from 'reactstrap'

// ** Styles
import '@styles/react/pages/page-authentication.scss'

const ToastContent = ({ t, name, role }) => {
  return (
    <div className='d-flex'>
      <div className='me-1'>
        <Avatar size='sm' color='success' icon={<Coffee size={12} />} />
      </div>
      <div className='d-flex flex-column'>
        <div className='d-flex justify-content-between'>
          <h6>{name}</h6>
          <X size={12} className='cursor-pointer' onClick={() => toast.dismiss(t.id)} />
        </div>
        <span>You have successfully logged in as an {role} user. Now you can start to explore. Enjoy!</span>
      </div>
    </div>
  )
}

const Login = () => {
  // ** Hooks
  const { isDark } = useSkin()
  const { login, logout } = useAuth({ handleRedirect: true })
  const navigate = useNavigate()
  const { instance, inProgress } = useMsal()
  const { user, ability } = useUserContext()
  const isDisabledAccount = !!user && ability.can('inactive', 'user')

  // ** ImageSrc
  const illustration = isDark ? 'login-v2-dark.svg' : 'login-v2.svg',
    source = require(`@images/pages/${illustration}`)

  const isLoading = inProgress !== InteractionStatus.None
  const isLoggingOut = inProgress === InteractionStatus.Logout
  const waitingMsg = isLoading && `${ isLoggingOut ? 'Logout' : 'Login'} in progress...`

  const onClickQuestion = () => {
    const newHref = mailTo({ to: 'customerrelations@unitasglobal.com', subject: 'Portal Inquiry' })
    window.location.href = newHref
  }

  const toHome = () => navigate('/')

  return (
    <UiLoader blocking={isLoading} loader={<Spinner />}>
    <div className='auth-wrapper auth-cover'>
      <Row className='auth-inner m-0'>
        <BrandLogo />
        <Col className='d-none d-lg-flex align-items-center p-5' lg='8' sm='12'>
          <div className='w-100 d-lg-flex align-items-center justify-content-center px-5'>
            <img className='img-fluid' src={source} alt='Login Cover' />
          </div>
        </Col>
        <Col className='d-flex align-items-center auth-bg px-2 p-lg-2' lg='4' sm='12'>
          <Col className='px-xl-2 mx-auto' sm='8' md='6' lg='12'>
            <AuthenticatedTemplate>
              {!isDisabledAccount 
                ? (
                  <>
                    <CardTitle tag='h2' className='fw-bold mb-1'>
                      You are currently logged in.
                    </CardTitle>
                    <p className="text-muted">
                      What would you like to do?  
                    </p>
                    <div className="d-flex flex-column justify-space-around gap-2 my-2">
                      <Button color="primary" size="lg" onClick={toHome}>Back to Home page</Button>
                      <Button size="lg" onClick={logout}>Log me out</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <CardTitle tag='h2' className='fw-bold mb-1'>
                      Your account has been deactivated.
                    </CardTitle>
                    <Alert className="p-2" color="warning">
                      An administrator has deactivated this user account. Please reach out to your organization's account manager or to our technical support team if you feel you are reaching this in error.
                    </Alert>
                    <div className="d-flex flex-column justify-space-around gap-2 mt-2 mb-4">
                      <Button size="lg" onClick={logout}>Log me out</Button>
                    </div>
                  </>
                )
              }
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <CardTitle tag='h3' className='fw-bold mb-1 text-center'>
                  Welcome to MyAccount,
                  <br/>
                  Unitas Global's Customer Portal!
                </CardTitle>
                <div className="m-2 p-1 border-info">
                  <div className="p-2 text-center">
                    {waitingMsg 
                      ? <Button disabled children={waitingMsg} />
                      : <Button color="primary" onClick={login} children="Sign In" />
                    }
                  </div>
                </div>
            </UnauthenticatedTemplate>
            <CardText className='mb-2 text-center' >
              <b>Need help? </b> 
              <span>Send us your portal </span>
              <Button color="link" style={{ display: 'contents' }} onClick={onClickQuestion} children=" questions" />
            </CardText>

            <div style={{ bottom: '3rem' }} className="start-0 w-100 position-absolute">
              <Footer />
            </div>
          </Col>
        </Col>
      </Row>
    </div>
    </UiLoader>
  )
}

export default Login
